import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './config/axios';
import './LocationTable.css';

/**
 * Interface for work area object structure within a location
 */
interface WorkArea {
  id: number;
  name: string;
  employees_count?: number;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for location object structure with work areas
 */
interface LocationWithWorkAreas {
  id: number;
  name: string;
  address?: string;
  radius_meters?: number;
  employees_count?: number;
  hours_ist?: number;
  hours_soll?: number;
  work_areas: WorkArea[];
  [key: string]: any; // Allow for additional properties that might come from the API
}

function LocationWorkAreaTable(): React.JSX.Element {
  const [locations, setLocations] = useState<LocationWithWorkAreas[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    // ACHTUNG: trailing slash am Ende
    axiosInstance
      .get<LocationWithWorkAreas[]>('/api/locations/with-workareas/')
      .then((res) => setLocations(res.data))
      .catch((err: any) => console.error('Error loading data:', err));
  }, []);

  // Edit Location => /locations/:id/edit
  const editLocation = (locationId: number): void => {
    navigate(`/locations/${locationId}/edit`);
  };

  // Edit WorkArea => /working-areas/:id/edit
  const editWorkArea = (workAreaId: number): void => {
    navigate(`/working-areas/${workAreaId}/edit`);
  };

  // Add new Location => /locations/new
  const handleAddLocation = (): void => {
    navigate('/locations/new');
  };

  return (
    <div className="table-container">
      <h2 style={{ marginBottom: '16px' }}>Locations &amp; WorkAreas</h2>

      <table className="my-locations-table" data-testid="locations-table">
        <thead>
          <tr>
            <th>Location</th>
            <th>Address</th>
            <th>Radius (m)</th>
            <th>WorkArea</th>
            <th>#Employees</th>
            <th>Hours (IST/SOLL)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((loc) => (
            <React.Fragment key={loc.id}>
              {/* Hauptzeile (Location) */}
              <tr className="location-main-row">
                <td>{loc.name}</td>
                <td>{loc.address || ''}</td>
                <td>{loc.radius_meters || 0}</td>
                <td></td>
                <td>{loc.employees_count || 0}</td>
                <td>{loc.hours_ist || 0} / {loc.hours_soll || 0}</td>
                <td>
                  <button
                    className="edit-button"
                    onClick={() => editLocation(loc.id)}
                  >
                    Edit Loc
                  </button>
                  <button
                    className="more-button"
                    style={{ marginLeft: '8px' }}
                  >
                    More
                  </button>
                </td>
              </tr>

              {/* Subrows => WorkAreas */}
              {loc.work_areas.map((wa) => (
                <tr key={wa.id} className="location-sub-row">
                  {/* leere Zellen, um WA etwas eingerückt zu zeigen */}
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ paddingLeft: '20px' }}>{wa.name}</td>
                  <td>{wa.employees_count || 0}</td>
                  <td>0 / 0</td> {/* or wa.hours_ist / wa.hours_soll if you want */}
                  <td>
                    <button
                      className="edit-button"
                      onClick={() => editWorkArea(wa.id)}
                    >
                      Edit WA
                    </button>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}

          {locations.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '10px' }}>
                No Locations found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* + Add Location Button */}
      <div style={{ marginTop: '16px' }}>
        <button
          className="create-button"
          onClick={handleAddLocation}
          style={{
            backgroundColor: '#002b80',
            color: '#fff',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          data-testid="add-location-button"
        >
          + Add Location
        </button>
      </div>
    </div>
  );
}

export default LocationWorkAreaTable; 